import React from "react";
import { Link } from "react-router-dom";
import "../css/privacy.scss";

const privacySections = [
  {
    title: "Who this policy covers",
    content: [
      "This policy applies when you browse the ioomparade store, add products to the cart, begin checkout, pay through the hosted payment page, contact us, or exercise a privacy right.",
      "ioomparade is responsible for the personal information described here except where a payment, hosting, carrier, or other provider acts under its own privacy notice.",
    ],
  },
  {
    title: "Information you provide",
    content: [
      "Checkout may collect your email address, name, company where supplied, delivery address, country or region, selected shipping method, cart contents, and order total. Messages may contain your name, email address, order reference, and any information you choose to include.",
      "Do not send full payment-card numbers, card security codes, passwords, or unrelated sensitive information by email.",
    ],
  },
  {
    title: "Information collected automatically",
    content: [
      "Like most online services, the site and its infrastructure may process IP address, device and browser type, language, approximate location derived from IP address, requested pages, timestamps, referral information, and security or error logs.",
      "The cart stores product identifiers, quantities, and selected variants in your browser's local storage. The store does not currently use advertising pixels or non-essential analytics cookies.",
    ],
  },
  {
    title: "How we use information",
    content: [
      "We use information to operate and secure the site; display and retain your cart; review, accept, fulfil, deliver, and support orders; process payment and refunds; prevent fraud and abuse; respond to enquiries and rights requests; maintain records; comply with law; and establish, exercise, or defend legal claims.",
      "We do not use the email entered at checkout for promotional marketing unless you separately make a clear marketing choice in a feature that identifies that purpose.",
    ],
  },
  {
    title: "Legal grounds where required",
    content: [
      "Where privacy law requires a legal basis, processing may be necessary to take steps at your request or perform a sales contract, comply with legal duties, or pursue legitimate interests such as site security, fraud prevention, customer support, and business recordkeeping. We rely on consent where the law requires it and you may withdraw that consent prospectively.",
    ],
  },
  {
    title: "Payment information",
    content: [
      "Card details are entered on the payment provider's hosted page, not into the ioomparade checkout form. We send the provider the information needed to create and reconcile the payment, such as an order reference, amount, currency, product title, and return page.",
      "The provider may process device, transaction, authentication, and fraud-prevention information under its own privacy notice. We may receive a payment status, transaction reference, refund information, or limited card details such as brand and last digits, but not the full card number or security code.",
    ],
  },
  {
    title: "When information is shared",
    content: [
      "Information may be provided on a need-to-know basis to hosting and infrastructure providers, payment and fraud-prevention services, fulfilment partners, carriers and customs intermediaries, customer-support tools, professional advisers, insurers, regulators, courts, and law-enforcement bodies where lawful.",
      "Information may also be transferred as part of a genuine financing, reorganization, sale, or transfer of the business, subject to appropriate confidentiality and legal requirements. We do not sell personal information or share it for cross-context behavioural advertising.",
    ],
  },
  {
    title: "International processing",
    content: [
      "The store serves multiple countries, so information may be processed in a location different from where you live. Privacy protections can differ between jurisdictions.",
      "Where required, we use an approved transfer mechanism or another lawful safeguard and limit the information to what the receiving service needs.",
    ],
  },
  {
    title: "Retention",
    content: [
      "We keep order, payment, delivery, support, and compliance records only for as long as reasonably needed for fulfilment, customer service, returns, accounting and tax duties, fraud prevention, dispute handling, enforcement, and other legal requirements.",
      "Retention periods differ by record and jurisdiction. Data may be kept longer when a complaint, investigation, legal hold, or unresolved transaction requires it, and is then deleted, anonymized, or securely isolated when appropriate.",
    ],
  },
  {
    title: "Security",
    content: [
      "We use reasonable administrative and technical measures designed to protect personal information and limit access to people and providers that need it. Payment-card entry is separated onto the provider's hosted page.",
      "No transmission or storage system is completely secure. Contact us promptly if you suspect misuse of your information, and review the Security Policy before submitting a vulnerability report.",
    ],
  },
  {
    title: "Your privacy rights",
    content: [
      "Depending on your location, you may ask to access, correct, delete, restrict, object to processing, withdraw consent, or receive a portable copy of personal information. You may also have rights to know the categories, sources, purposes, and recipients involved, or to appeal a denied request.",
      "We may need to verify identity and authority before responding. Some information may be retained or withheld where an exception applies, including to complete a transaction, protect security, keep required records, or preserve another person's rights.",
    ],
  },
  {
    title: "Automated checks",
    content: [
      "The payment provider and related fraud-prevention services may use automated signals to authenticate or assess a transaction. A payment may be declined, delayed, or sent for review. Contact us if you believe an order was incorrectly affected, although the payment provider may need to address decisions made within its service.",
    ],
  },
  {
    title: "Children's privacy",
    content: [
      "The store is intended for adults and is not directed to children. We do not knowingly collect personal information from a child who cannot lawfully provide it. A parent or guardian who believes a child supplied information should contact us so we can review and delete it where appropriate.",
    ],
  },
  {
    title: "Cookies and browser storage",
    content: [
      "Essential browser storage keeps selected cart items available as you move between pages. We do not currently set non-essential advertising or analytics cookies. The separate Cookie Policy identifies the storage used and explains browser controls.",
    ],
  },
  {
    title: "Changes and contact",
    content: [
      "We may update this policy to reflect changes in the store, providers, legal duties, or data practices. A revised version applies from the effective date shown on this page; material changes will be highlighted or notified where required.",
      "Questions, complaints, and rights requests can be sent to globe@ioomparade.online. Use the subject “Privacy Request” for a data-rights request.",
    ],
  },
];

function Privacy() {
  return (
    <main className="ink-info-page ink-info-page--privacy">
      <div className="ink-info-page__inner">
        <header className="ink-info-page__header">
          <p className="ink-info-page__eyebrow">Your information matters</p>
          <h1>Privacy Policy</h1>
          <p className="ink-info-page__lead">
            This policy explains how ioomparade collects, uses, shares, and
            protects information when you browse, contact us, or place an order.
          </p>
          <p className="policy-updated">
            Effective and last updated: <time dateTime="2026-10-16">October 16, 2026</time>
          </p>
        </header>

        <div className="privacy-layout">
          <nav className="privacy-index" aria-label="Privacy policy sections">
            <p>On this page</p>
            <ol>
              {privacySections.map((section, index) => (
                <li key={section.title}>
                  <a href={`#privacy-section-${index + 1}`}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
            <Link to="/cookies">Read the Cookie Policy</Link>
          </nav>

          <div className="privacy-sections">
            {privacySections.map((section, index) => (
              <section className="privacy-section" id={`privacy-section-${index + 1}`} key={section.title}>
                <span className="privacy-section__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2>{section.title}</h2>
                  {section.content.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        <section className="privacy-contact" aria-labelledby="privacy-contact-title">
          <div>
            <p className="ink-info-page__kicker">Questions and requests</p>
            <h2 id="privacy-contact-title">Contact ioomparade</h2>
            <p>For a privacy request or complaint, contact us by email.</p>
          </div>
          <address>
            <a href="mailto:globe@ioomparade.online">globe@ioomparade.online</a>
            <span>ioomparade — Privacy Request</span>
            <span>8299 Parkwood Pl, Jonesboro, GA 30238</span>
          </address>
        </section>
      </div>
    </main>
  );
}

export default Privacy;
